import { noop } from 'lodash';
import { createContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { EntityDetailSuggestion } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  MatchableKind,
  SuggestableKind,
} from '@/modules/aiSuggestions/aiSuggestions.types';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { AiSuggestionKind } from '@/types/schema';

/**
 * Use this context to provide the form with suggested data. This should be
 * used within a form context provider.
 */
export interface EntitySuggestionsContext {
  /**
   * This includes suggestions that have been acknowledged and unacknowledged.
   */
  suggestionsByKind: Record<AiSuggestionKind, EntityDetailSuggestion[]>;

  /**
   * The available suggestions for the given entity section being edited.
   */
  getCurrentSectionSuggestions: (
    currentSection: EditEntitySection
  ) => EntityDetailSuggestion[];

  /**
   * Returns the suggestable suggestion of a specific kind.
   */
  getSuggestableSuggestion: (
    kind: SuggestableKind
  ) => EntityDetailSuggestion | undefined;

  /**
   * Returns the suggestion of a specific kind that got matched with the given id.
   */
  getMatchableSuggestion: (
    kind: MatchableKind,
    matchedId: string
  ) => EntityDetailSuggestion | undefined;

  /**
   * Refetch the suggestions for the current entity, or the given entityID, and
   * updates the state variables.
   */
  refetchSuggestions: (variables?: { entityID: string }) => void;

  loading: boolean;

  /**
   * Document IDs extracted from the file paragraphs on suggestion evidence.
   */
  suggestionDocumentIds: string[];
}

export const getDefaultValues = (): EntitySuggestionsContext => ({
  suggestionsByKind: {} as Record<AiSuggestionKind, EntityDetailSuggestion[]>,
  getCurrentSectionSuggestions: () => [],
  getSuggestableSuggestion: () => undefined,
  getMatchableSuggestion: () => undefined,
  refetchSuggestions: noop,
  loading: true,
  suggestionDocumentIds: [],
});

export const EntitySuggestionsContext =
  createContext<EntitySuggestionsContext>(getDefaultValues());

export const useEntitySuggestionsContext = () => {
  return useGuardedContext(
    EntitySuggestionsContext,
    'EntitySuggestionsProvider'
  );
};
